
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";

/**
 * Hook to track product views by the current user
 * This will automatically insert a view record when a product is viewed
 * and update the total view count via a database trigger
 * 
 * @param productId - The ID of the product being viewed
 */
export function useProductViews(productId: string | undefined) {
  const { user } = useAuth();

  useEffect(() => {
    // Only track views if we have both a user and a product ID
    if (!user || !productId) return;

    const trackProductView = async () => {
      try {
        // Insert a view record - the database trigger will handle the logic
        // for updating existing records and incrementing view counts
        const { error } = await supabase
          .from('retailer_product_views')
          .insert({
            user_id: user.id,
            product_id: productId
          });

        if (error) {
          console.error('Error tracking product view:', error);
          
          // Don't show toast errors to end users for view tracking failures
          // as this is a background operation that shouldn't interrupt UX
        }
      } catch (err) {
        console.error('Failed to track product view:', err);
      }
    };

    // Track the view when the component mounts
    trackProductView();
    
    // Only track the view once when the component mounts
    // or when the user or productId changes
  }, [user, productId]);
}

/**
 * Hook to fetch the total view count for a product
 * 
 * @param productId - The ID of the product
 * @returns The total view count for the product
 */
export function useProductViewCount(productId: string | undefined) {
  const [viewCount, setViewCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  
  useEffect(() => {
    if (!productId) {
      setLoading(false);
      return;
    }
    
    const fetchViewCount = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('products')
          .select('total_views')
          .eq('id', productId)
          .single();
          
        if (error) {
          console.error('Error fetching view count:', error);
          return;
        }
        
        setViewCount(data?.total_views || 0);
      } catch (err) {
        console.error('Failed to fetch view count:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchViewCount();
  }, [productId]);
  
  return { viewCount, loading };
}
